import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Input,
  Tooltip,
  HStack,
  VStack,
  Text,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Progress,
  useDisclosure,
  Icon,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { Header, ColumnInfo, ModelInfo, ObservationColumnProto } from "build-ts/metadata";
import { AggregationType, ColumnType } from "build-ts/enums";
import CreateKGColumnComponent from "modules/kgcolumn/containers/KGColumnCreationForm";
import MetricDetailFormContainer from "modules/metric/conatiners/MetricDetailForm";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { useNavigate } from "react-router-dom";
import { AppIcons } from "constants/icons";
import OntologyMappingLeftPanel, { UnmappedSortOption } from "./OntologyMappingLeftPanel";
import OntologyMappingRightPanel, { MappedSortOption } from "./OntologyMappingRightPanel";
import { iconForKgColumn, kgCreateModalTitle } from "modules/mkg/utils/ontologyEntityIcons";

interface OntologyMappingProps {
  loading: boolean;
  modelInfo?: ModelInfo | undefined;
  addedColumns?: ColumnInfo[] | undefined;
  removedColumns?: ColumnInfo[] | undefined;
  header: Header;
  columns: ColumnInfo[];
  kgColumns: Header[];
  dependentKgColumns: Map<string, Header[]>;
  dependentMetrics: Map<string, Header[]>;
  handleHeaderChange: (property: string, value: string) => void;
  handleColumnChange: (
    index: number,
    property: "data_type" | "column_type" | "default_aggregation",
    value: string
  ) => void;
  onKgSelectionChange: (columnIndex: number, kgColumn: Header | null) => void;
  onMetricCreated: (columnIndex: number, header: Header) => void;
  onNewKgColumnCreated?: (header: Header) => void;
  onDeleteMetricRequest?: (metricId: string) => Promise<Header[] | null>;
  handleSubmit: () => void;
  onDiscard?: () => Promise<void>;
  updateFlag: boolean;
  hasUnsavedChanges?: boolean;
  hasDiscardableChanges?: boolean;
  unsavedMappingIds?: Set<string>;
  buttonLoader: boolean;
  discardLoader?: boolean;
  refreshKGColumns: () => Promise<Header[]>;
  handleQueueMetricRequest: (request: CreateOrUpdateMetrics_Request) => void;
  metricRequestList: CreateOrUpdateMetrics_Request[];
  onBack: () => void;
}

function columnIndexById(columns: ColumnInfo[], id: string | undefined): number {
  if (!id) return -1;
  return columns.findIndex(c => c.header?.id === id);
}

function matchesSearch(query: string, name: string | undefined): boolean {
  const q = query.trim().toLowerCase();
  return !q || (name || "").toLowerCase().includes(q);
}

function headerSortMs(h: Header | undefined, kind: "created" | "modified"): number {
  if (!h) return 0;
  if (kind === "modified") {
    const m = h.last_modified_date_ms ?? h.created_date_ms;
    return m !== null ? Number(m) : 0;
  }
  const c = h.created_date_ms;
  return c !== null ? Number(c) : 0;
}

const MAP_HEADER_ENTITY_TOOLTIP_MAX = 25;

function MapHeaderHoverEntityList({ title, items }: { title: string; items: string[] }) {
  const shown = items.slice(0, MAP_HEADER_ENTITY_TOOLTIP_MAX);
  const rest = items.length - shown.length;
  return (
    <Box maxW="280px" maxH="220px" overflowY="auto" py={0.5}>
      <Text fontWeight="700" fontSize="xs" mb={1} color="white">
        {title}
      </Text>
      {shown.map((line, i) => (
        <Text key={i} fontSize="xs" color="whiteAlpha.900" mb={0} lineHeight="1.35">
          • {line}
        </Text>
      ))}
      {rest > 0 ? (
        <Text fontSize="xs" color="whiteAlpha.700" mt={1} mb={0}>
          …and {rest} more
        </Text>
      ) : null}
    </Box>
  );
}

/** Icon + count + type label; tooltip lists entities. Clickable to filter the right panel. */
function MapHeaderStatChip({
  icon,
  value,
  name,
  tooltipTitle,
  names,
  ariaLabel,
  onClick,
  isActive,
  chipColorScheme = "purple",
}: {
  icon: React.ElementType;
  value: number;
  name: string;
  tooltipTitle: string;
  names: string[];
  ariaLabel: string;
  onClick?: () => void;
  isActive?: boolean;
  chipColorScheme?: string;
}) {
  const typeLabel = value === 1 ? name : name + "s";
  return (
    <Tooltip
      label={<MapHeaderHoverEntityList title={tooltipTitle} items={names} />}
      hasArrow
      openDelay={250}
      placement="top"
      bg="gray.700"
    >
      <HStack
        spacing={1.5}
        as="button"
        align="center"
        py={1}
        px={2}
        borderRadius="md"
        borderColor={`${chipColorScheme}.600`}
        bg={isActive ? `${chipColorScheme}.50` : "transparent"}
        borderWidth={isActive ? "2px" : "1px"}
        flexShrink={0}
        cursor="pointer"
        onClick={onClick}
        aria-label={`${ariaLabel}: ${value}${isActive ? " (filter active)" : ""}`}
        aria-pressed={isActive}
        _hover={{ bg: isActive ? `${chipColorScheme}.100` : "gray.50", borderColor: `${chipColorScheme}.400` }}
        transition="background 0.15s, border-color 0.15s"
      >
        <Icon
          as={icon}
          boxSize={4}
          color={isActive ? `${chipColorScheme}.600` : `${chipColorScheme}.700`}
          aria-hidden
          flexShrink={0}
        />
        <Text
          fontSize="sm"
          fontWeight="700"
          color={isActive ? `${chipColorScheme}.600` : `${chipColorScheme}.700`}
          mb={0}
          lineHeight="1"
        >
          {value}
        </Text>
        <Text
          fontSize="sm"
          fontWeight="600"
          color={isActive ? `${chipColorScheme}.600` : `${chipColorScheme}.700`}
          mb={0}
          lineHeight="1"
        >
          {typeLabel}
        </Text>
      </HStack>
    </Tooltip>
  );
}

const OntologyMapping: React.FC<OntologyMappingProps> = ({
  loading,
  modelInfo,
  addedColumns,
  removedColumns,
  header,
  columns,
  kgColumns,
  dependentKgColumns,
  dependentMetrics,
  handleHeaderChange,
  handleColumnChange,
  onKgSelectionChange,
  onMetricCreated,
  onNewKgColumnCreated,
  onDeleteMetricRequest,
  handleSubmit,
  onDiscard,
  updateFlag,
  hasUnsavedChanges,
  hasDiscardableChanges,
  unsavedMappingIds,
  buttonLoader,
  discardLoader,
  refreshKGColumns,
  handleQueueMetricRequest,
  metricRequestList,
  onBack,
}) => {
  const navigate = useNavigate();
  const [unmappedSearchQuery, setUnmappedSearchQuery] = useState("");
  const [unmappedSort, setUnmappedSort] = useState<UnmappedSortOption>("default");
  const [mappedSearchQuery, setMappedSearchQuery] = useState("");
  const [mappedSortOption, setMappedSortOption] = useState<MappedSortOption>("mapping");
  const [chipFilter, setChipFilter] = useState<"metric" | "attribute" | "measure" | "timeframe" | null>(null);
  const toggleChipFilter = useCallback((value: "metric" | "attribute" | "measure" | "timeframe") => {
    setChipFilter(prev => (prev === value ? null : value));
  }, []);
  const [pendingPromoteIds, setPendingPromoteIds] = useState<Set<string>>(() => new Set());
  const [rightPanelIds, setRightPanelIds] = useState<string[]>([]);
  const [dismissedRemovedIds, setDismissedRemovedIds] = useState<Set<string>>(() => new Set());
  const [removedSectionOpen, setRemovedSectionOpen] = useState(false);
  const [autoOpenColumnId, setAutoOpenColumnId] = useState<string | null>(null);

  const { isOpen: isKGModalOpen, onOpen: onKGModalOpen, onClose: onKGModalClose } = useDisclosure();
  const [kgModalColumnIndex, setKgModalColumnIndex] = useState<number | null>(null);
  const [kgModalColumnType, setKgModalColumnType] = useState<ColumnType>(ColumnType.ATTRIBUTE);
  const [kgModalDefaultName, setKgModalDefaultName] = useState<string>("");

  const handleOpenCreateKGColumnModal = (columnType: ColumnType, columnIndex: number, defaultName: string) => {
    setKgModalColumnIndex(columnIndex);
    setKgModalColumnType(columnType);
    setKgModalDefaultName(defaultName);
    onKGModalOpen();
  };

  const handleKGColumnCreated = (createdHeader: Header) => {
    if (kgModalColumnIndex !== null) {
      onKgSelectionChange(kgModalColumnIndex, createdHeader);
    }
    onNewKgColumnCreated?.(createdHeader);
    onKGModalClose();
  };

  const { isOpen: isMetricModalOpen, onOpen: onMetricModalOpen, onClose: onMetricModalClose } = useDisclosure();
  const [metricModalColumnIndex, setMetricModalColumnIndex] = useState<number | null>(null);
  const [metricModalLogicalColumn, setMetricModalLogicalColumn] = useState<ColumnInfo | null>(null);

  const handleOpenCreateMetricModal = (columnIndex: number, logicalColumn: ColumnInfo) => {
    setMetricModalColumnIndex(columnIndex);
    setMetricModalLogicalColumn(logicalColumn);
    onMetricModalOpen();
  };

  // Case 1 (model exists, updateFlag=true): metric was created via API
  const handleMetricCreated = (createdHeader: Header) => {
    if (metricModalColumnIndex !== null) {
      onMetricCreated(metricModalColumnIndex, createdHeader);
    }
    onMetricModalClose();
  };

  // Case 2 (model not yet saved, updateFlag=false): queue the full request
  const handleMetricDeferred = (formData: CreateOrUpdateMetrics_Request) => {
    handleQueueMetricRequest(formData);
    onMetricModalClose();
  };

  const addedColumnIds = useMemo(
    () => new Set((addedColumns ?? []).map(c => c.header?.id).filter((id): id is string => Boolean(id))),
    [addedColumns]
  );

  // Combined metrics: existing from backend + newly queued (pre-save)
  const getCombinedMetrics = useCallback(
    (columnId: string): Header[] => {
      const existingMetrics = dependentMetrics.get(columnId) || [];
      const newMetrics: Header[] = [];
      metricRequestList.forEach(request => {
        request.metrics?.forEach(metric => {
          const observationColumns = metric.definition?.observation_columns || [];
          const isForThisColumn = observationColumns.some(obsCol => obsCol.logical_column_header?.id === columnId);
          if (isForThisColumn && metric.header) {
            newMetrics.push(metric.header);
          }
        });
      });
      return [...existingMetrics, ...newMetrics];
    },
    [dependentMetrics, metricRequestList]
  );

  const isLogicalMapped = useCallback(
    (col: ColumnInfo) => Boolean(dependentKgColumns.get(col.header?.id || "")?.[0]),
    [dependentKgColumns]
  );

  // A column belongs in the right panel if it has a KG column OR at least one metric.
  const isInRightPanel = useCallback(
    (col: ColumnInfo) => isLogicalMapped(col) || getCombinedMetrics(col.header?.id ?? "").length > 0,
    [isLogicalMapped, getCombinedMetrics]
  );

  const updatedColumnIds = useMemo(() => {
    const set = new Set<string>();
    if (!modelInfo?.columns?.length) return set;
    const byId = new Map(modelInfo.columns.map(c => [c.header?.id, c] as const));
    for (const c of columns) {
      const id = c.header?.id;
      if (!id) continue;
      const orig = byId.get(id);
      if (orig && orig.data_type !== c.data_type) set.add(id);
    }
    return set;
  }, [columns, modelInfo]);

  const totalNonRemoved = columns.length;

  const rightPanelRowCount = useMemo(() => {
    let n = 0;
    for (const id of rightPanelIds) {
      const idx = columnIndexById(columns, id);
      if (idx < 0) continue;
      if (isInRightPanel(columns[idx]) || pendingPromoteIds.has(id)) n += 1;
    }
    return n;
  }, [rightPanelIds, columns, isInRightPanel, pendingPromoteIds]);

  const progressPct = totalNonRemoved > 0 ? Math.min(100, Math.round((rightPanelRowCount / totalNonRemoved) * 100)) : 0;

  const leftPanelCount = totalNonRemoved - rightPanelRowCount;

  const visibleRemovedColumns = useMemo(
    () => (removedColumns ?? []).filter(c => c.header?.id && !dismissedRemovedIds.has(c.header.id)),
    [removedColumns, dismissedRemovedIds]
  );

  const mappedVisibleIds = useMemo(() => {
    const filtered = rightPanelIds.filter(id => {
      const idx = columnIndexById(columns, id);
      if (idx < 0) return false;
      const col = columns[idx];
      if (!matchesSearch(mappedSearchQuery, col.header?.name)) return false;
      if (!(isInRightPanel(col) || pendingPromoteIds.has(id))) return false;
      if (chipFilter) {
        const kgCol = dependentKgColumns.get(id)?.[0] ?? null;
        switch (chipFilter) {
          case "metric":
            return getCombinedMetrics(id).length > 0;
          case "attribute":
            return kgCol?.column_type === ColumnType.ATTRIBUTE;
          case "measure":
            return kgCol?.column_type === ColumnType.MEASURE;
          case "timeframe":
            return kgCol?.column_type === ColumnType.TEMPORAL;
        }
      }
      return true;
    });

    if (mappedSortOption === "mapping") return filtered;

    const enriched = filtered
      .map(id => {
        const idx = columnIndexById(columns, id);
        if (idx < 0) return null;
        return { id, col: columns[idx] };
      })
      .filter((x): x is { id: string; col: ColumnInfo } => x !== null);

    const getName = (c: ColumnInfo) => (c.header?.name || "").toLowerCase();
    const arr = [...enriched];
    switch (mappedSortOption) {
      case "name-asc":
        arr.sort((a, b) => getName(a.col).localeCompare(getName(b.col)));
        break;
      case "name-desc":
        arr.sort((a, b) => getName(b.col).localeCompare(getName(a.col)));
        break;
      case "created":
        arr.sort((a, b) => headerSortMs(b.col.header, "created") - headerSortMs(a.col.header, "created"));
        break;
      case "modified":
        arr.sort((a, b) => headerSortMs(b.col.header, "modified") - headerSortMs(a.col.header, "modified"));
        break;
      default:
        break;
    }
    return arr.map(x => x.id);
  }, [
    rightPanelIds,
    columns,
    pendingPromoteIds,
    isInRightPanel,
    mappedSearchQuery,
    mappedSortOption,
    chipFilter,
    dependentKgColumns,
    getCombinedMetrics,
  ]);

  const unmappedDisplayEntries = useMemo(() => {
    const entries: { column: ColumnInfo; index: number }[] = [];
    columns.forEach((c, i) => {
      if (isInRightPanel(c)) return;
      if (!matchesSearch(unmappedSearchQuery, c.header?.name)) return;
      entries.push({ column: c, index: i });
    });
    if (unmappedSort === "name-asc") {
      entries.sort((a, b) =>
        (a.column.header?.name || "").localeCompare(b.column.header?.name || "", undefined, {
          sensitivity: "base",
        })
      );
    } else if (unmappedSort === "name-desc") {
      entries.sort((a, b) =>
        (b.column.header?.name || "").localeCompare(a.column.header?.name || "", undefined, {
          sensitivity: "base",
        })
      );
    }
    return entries;
  }, [columns, isInRightPanel, unmappedSearchQuery, unmappedSort]);

  const mapHeaderEntityStats = useMemo(() => {
    const metricHeaders: Header[] = [];
    const seenMetric = new Set<string>();
    for (const list of Array.from(dependentMetrics.values())) {
      for (const h of list ?? []) {
        const mid = h.id;
        if (mid && !seenMetric.has(mid)) {
          seenMetric.add(mid);
          metricHeaders.push(h);
        }
      }
    }

    const attributeLinkLabels: string[] = [];
    const measureLinkLabels: string[] = [];
    const temporalLinkLabels: string[] = [];

    const colNameById = new Map<string, string>();
    for (const c of columns) {
      const cid = c.header?.id;
      if (cid) colNameById.set(cid, c.header?.name?.trim() || "—");
    }

    for (const [lcId, kgList] of Array.from(dependentKgColumns.entries())) {
      const lcName = colNameById.get(lcId) ?? "—";
      for (const kg of kgList ?? []) {
        const kgName = kg.name?.trim() || "Unknown KG column";
        const line = `${kgName} · ${lcName}`;
        const ct = kg.column_type;
        if (ct === ColumnType.ATTRIBUTE) attributeLinkLabels.push(line);
        else if (ct === ColumnType.MEASURE) measureLinkLabels.push(line);
        else if (ct === ColumnType.TEMPORAL) temporalLinkLabels.push(line);
      }
    }

    return {
      metricCount: metricHeaders.length,
      metricNames: metricHeaders.map(h => h.name?.trim() || h.id || "Unnamed metric"),
      attributeLinkLabels,
      measureLinkLabels,
      temporalLinkLabels,
    };
  }, [header?.id, dependentMetrics, dependentKgColumns, columns]);

  useEffect(() => {
    if (loading) return;
    setRightPanelIds(prev => {
      // Seed right panel with columns that have a KG column OR at least one metric
      const candidateEntries = columns
        .map((c, i) => ({ id: c.header?.id, i }))
        .filter((x): x is { id: string; i: number } => Boolean(x.id && isInRightPanel(columns[x.i])))
        .sort((a, b) => b.i - a.i);
      const seen = new Set(prev);
      let next = [...prev];
      for (const { id } of candidateEntries) {
        if (!seen.has(id)) {
          next.unshift(id);
          seen.add(id);
        }
      }
      next = next.filter(id => {
        const idx = columnIndexById(columns, id);
        if (idx < 0) return false;
        return isInRightPanel(columns[idx]) || pendingPromoteIds.has(id);
      });
      return next;
    });
  }, [loading, columns, isInRightPanel, pendingPromoteIds]);

  const handlePromoteFromLeft = useCallback(
    (column: ColumnInfo, index: number) => {
      const id = column.header?.id;
      if (!id) return;
      // Toggle: clicking a pending-but-unmapped column again removes it from the right panel.
      if (pendingPromoteIds.has(id)) {
        setPendingPromoteIds(p => {
          const n = new Set(p);
          n.delete(id);
          return n;
        });
        setRightPanelIds(ids => ids.filter(x => x !== id));
        setAutoOpenColumnId(prev => (prev === id ? null : prev));
      } else {
        setPendingPromoteIds(p => new Set(p).add(id));
        setRightPanelIds(ids => [id, ...ids.filter(x => x !== id)]);
        setAutoOpenColumnId(id);
      }
    },
    [pendingPromoteIds]
  );

  const handleRightSelectorMenuClose = useCallback((columnId: string, hasKgSelection: boolean) => {
    setAutoOpenColumnId(prev => (prev === columnId ? null : prev));
    // Only remove from pendingPromoteIds when a KG column was actually selected.
    // Otherwise, the column stays in the right panel so the user can map it at any time.
    if (hasKgSelection) {
      setPendingPromoteIds(p => {
        const n = new Set(p);
        n.delete(columnId);
        return n;
      });
    }
  }, []);

  const tsWorksheetId = modelInfo?.data_source?.ts_worksheet_id;

  return (
    <Box flex={1} minH={0} h="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">
      {loading ? (
        <Flex flex={1} minH={0} justify="center" align="center" gap={3}>
          <Spinner size="md" color="purple.500" thickness="2px" />
          <Text color="gray.600" fontSize="md" m={0}>
            Loading model information...
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" flex={1} minH={0} overflow="hidden">
          {/* Fixed toolbar (does not scroll with rows) */}
          <Box
            flexShrink={0}
            zIndex={20}
            bg="white"
            borderBottomWidth="1px"
            borderColor="gray.200"
            px={{ base: 3, md: 6 }}
            py={3}
            mb={4}
            boxShadow="sm"
          >
            <Flex align="center" justify="space-between" gap={4} flexWrap="wrap" mb={3}>
              <Breadcrumb separator={<ChevronRightIcon color="gray.400" boxSize={3} />} fontSize="sm" flex="1" minW={0}>
                <BreadcrumbItem>
                  <BreadcrumbLink onClick={() => navigate("/mkg/models")} color="purple.600">
                    Data Models
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <Text as="span" color="gray.700" fontWeight="600" mb={0}>
                    {header?.name || "Model"}
                  </Text>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <Text as="span" color="gray.500" mb={0}>
                    Map
                  </Text>
                </BreadcrumbItem>
              </Breadcrumb>
              {/* Top-right buttons: Back, Edit Model, Save Mapping */}
              <HStack spacing={2} flexShrink={0}>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  onClick={onBack}
                  leftIcon={<Text mb={0}>←</Text>}
                  size="md"
                >
                  Back
                </Button>
                <Tooltip label="Add Formula Column" hasArrow bg="gray.700" color="white" fontSize="xs">
                  <Button
                    aria-label="Add Formula Column"
                    leftIcon={<Icon as={AppIcons.mathFunction} boxSize={5} />}
                    variant="solid"
                    size="md"
                    colorScheme="purple"
                    isDisabled={!tsWorksheetId}
                    onClick={() => tsWorksheetId && navigate(`/mkg/thoughtspot/${tsWorksheetId}`)}
                  >
                    <Text mb={0} fontWeight="700">
                      Edit Model
                    </Text>
                  </Button>
                </Tooltip>

                {hasDiscardableChanges && (
                  <Button
                    colorScheme="red"
                    variant="outline"
                    onClick={onDiscard}
                    isLoading={discardLoader}
                    isDisabled={buttonLoader}
                    size="md"
                    leftIcon={<Icon as={AppIcons.closeLine} boxSize={4} />}
                  >
                    <Text mb={0} fontWeight="700">
                      Discard
                    </Text>
                  </Button>
                )}
                {(!updateFlag || hasUnsavedChanges) && (
                  <Button
                    colorScheme="green"
                    bg="green.600"
                    color="white"
                    _hover={{ bg: "green.700" }}
                    onClick={handleSubmit}
                    isLoading={buttonLoader}
                    isDisabled={discardLoader}
                    size="md"
                    leftIcon={<Icon as={AppIcons.save} boxSize={5} />}
                  >
                    <Text mb={0} fontWeight="700">
                      Save Mapping
                    </Text>
                  </Button>
                )}
              </HStack>
            </Flex>
            {/* Name and Description of the model */}
            <VStack align="stretch" spacing={0} pt={1} borderTopWidth="1px" borderColor="gray.100">
              <Flex align="center" gap={3} flexWrap="wrap" py={2} w="100%">
                <HStack align="center" spacing={2} flex={{ base: "1 1 100%", md: "1 1 0" }} minW={0}>
                  <Text fontSize="xs" fontWeight="700" color="gray.500" mb={0} whiteSpace="nowrap">
                    Name
                  </Text>
                  <Input
                    flex={1}
                    minW={0}
                    placeholder="Model name"
                    value={header?.name || ""}
                    onChange={e => handleHeaderChange("name", e.target.value)}
                    fontWeight="600"
                    size="sm"
                  />
                </HStack>
                <HStack align="center" spacing={2} flex={{ base: "1 1 100%", md: "1 1 0" }} minW={0}>
                  <Text fontSize="xs" fontWeight="700" color="gray.500" mb={0} whiteSpace="nowrap">
                    Description
                  </Text>
                  <Input
                    flex={1}
                    minW={0}
                    placeholder="Model description"
                    value={header?.description || ""}
                    onChange={e => handleHeaderChange("description", e.target.value)}
                    fontSize="sm"
                    size="sm"
                  />
                </HStack>
              </Flex>

              {/* Progress Bar */}
              <Flex
                w="100%"
                align="center"
                justify="space-between"
                gap={3}
                flexWrap="wrap"
                py={2}
                borderTopWidth="1px"
                borderColor="gray.100"
              >
                <HStack
                  spacing={3}
                  align="center"
                  flexShrink={0}
                  minW={0}
                  borderWidth="1px"
                  borderColor="gray.300"
                  borderRadius="full"
                  p={1}
                >
                  <Box
                    w={{ base: "min(100%, 240px)", sm: "240px" }}
                    maxW="240px"
                    minW={{ base: "200px", sm: "200px" }}
                    flexShrink={0}
                    p={1}
                  >
                    <Progress
                      w="100%"
                      value={progressPct}
                      size="sm"
                      colorScheme="green"
                      borderRadius="full"
                      bg="gray.200"
                    />
                  </Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.700" mb={0} mr={1}>
                    {rightPanelRowCount} / {totalNonRemoved} {rightPanelRowCount > 1 ? "columns" : "column"} in ontology
                  </Text>
                </HStack>
                <HStack
                  spacing={2}
                  flexWrap="wrap"
                  justify="flex-end"
                  flex={{ base: "1 1 100%", md: "0 0 auto" }}
                  minW={0}
                >
                  {mapHeaderEntityStats.metricCount > 0 ? (
                    <MapHeaderStatChip
                      icon={AppIcons.metric}
                      value={mapHeaderEntityStats.metricCount}
                      name="Metric"
                      tooltipTitle="Metrics"
                      names={mapHeaderEntityStats.metricNames}
                      ariaLabel="Metrics on this model"
                      onClick={() => toggleChipFilter("metric")}
                      isActive={chipFilter === "metric"}
                      chipColorScheme="purple"
                    />
                  ) : null}
                  {mapHeaderEntityStats.attributeLinkLabels.length > 0 ? (
                    <MapHeaderStatChip
                      icon={AppIcons.attribute}
                      value={mapHeaderEntityStats.attributeLinkLabels.length}
                      name="Attribute"
                      tooltipTitle="Attributes"
                      names={mapHeaderEntityStats.attributeLinkLabels}
                      ariaLabel="Attribute links in knowledge graph"
                      onClick={() => toggleChipFilter("attribute")}
                      isActive={chipFilter === "attribute"}
                      chipColorScheme="blue"
                    />
                  ) : null}
                  {mapHeaderEntityStats.measureLinkLabels.length > 0 ? (
                    <MapHeaderStatChip
                      icon={AppIcons.measure}
                      value={mapHeaderEntityStats.measureLinkLabels.length}
                      name="Measure"
                      tooltipTitle="Measures"
                      names={mapHeaderEntityStats.measureLinkLabels}
                      ariaLabel="Measure links in knowledge graph"
                      onClick={() => toggleChipFilter("measure")}
                      isActive={chipFilter === "measure"}
                      chipColorScheme="green"
                    />
                  ) : null}
                  {mapHeaderEntityStats.temporalLinkLabels.length > 0 ? (
                    <MapHeaderStatChip
                      icon={AppIcons.timeframe}
                      value={mapHeaderEntityStats.temporalLinkLabels.length}
                      name="Timeframe"
                      tooltipTitle="Timeframes"
                      names={mapHeaderEntityStats.temporalLinkLabels}
                      ariaLabel="Datetime links in knowledge graph"
                      onClick={() => toggleChipFilter("timeframe")}
                      isActive={chipFilter === "timeframe"}
                      chipColorScheme="yellow"
                    />
                  ) : null}
                </HStack>
              </Flex>
            </VStack>
          </Box>

          {/* Two-panel split: unmapped | mapped */}
          <Flex
            flex={1}
            minH={0}
            overflow="hidden"
            px={{ base: 3, md: 6 }}
            pb={4}
            gap={0}
            direction={{ base: "column", md: "row" }}
            align="stretch"
          >
            <OntologyMappingLeftPanel
              leftPanelCount={leftPanelCount}
              unmappedDisplayEntries={unmappedDisplayEntries}
              unmappedSort={unmappedSort}
              setUnmappedSort={setUnmappedSort}
              unmappedSearchQuery={unmappedSearchQuery}
              setUnmappedSearchQuery={setUnmappedSearchQuery}
              addedColumnIds={addedColumnIds}
              updatedColumnIds={updatedColumnIds}
              pendingPromoteIds={pendingPromoteIds}
              getCombinedMetrics={getCombinedMetrics}
              handlePromoteFromLeft={handlePromoteFromLeft}
              handleColumnChange={handleColumnChange}
            />
            <OntologyMappingRightPanel
              rightPanelRowCount={rightPanelRowCount}
              mappedVisibleIds={mappedVisibleIds}
              mappedSearchQuery={mappedSearchQuery}
              setMappedSearchQuery={setMappedSearchQuery}
              mappedSortOption={mappedSortOption}
              setMappedSortOption={setMappedSortOption}
              columns={columns}
              isLogicalMapped={isLogicalMapped}
              pendingPromoteIds={pendingPromoteIds}
              addedColumnIds={addedColumnIds}
              updatedColumnIds={updatedColumnIds}
              kgColumns={kgColumns}
              dependentKgColumns={dependentKgColumns}
              getCombinedMetrics={getCombinedMetrics}
              onKgSelectionChange={onKgSelectionChange}
              handleOpenCreateKGColumnModal={handleOpenCreateKGColumnModal}
              handleOpenCreateMetricModal={handleOpenCreateMetricModal}
              autoOpenColumnId={autoOpenColumnId}
              handleRightSelectorMenuClose={handleRightSelectorMenuClose}
              visibleRemovedColumns={visibleRemovedColumns}
              removedSectionOpen={removedSectionOpen}
              setRemovedSectionOpen={setRemovedSectionOpen}
              setDismissedRemovedIds={setDismissedRemovedIds}
              unsavedMappingIds={unsavedMappingIds}
              onDeleteMetricRequest={onDeleteMetricRequest}
            />
          </Flex>
        </Flex>
      )}

      <Modal isOpen={isKGModalOpen} onClose={onKGModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display="flex" alignItems="center" gap={3} pr={10}>
            <Icon as={iconForKgColumn(kgModalColumnType)} boxSize={6} color="purple.600" flexShrink={0} aria-hidden />
            <Text as="span" fontSize="lg" fontWeight="bold" mb={0}>
              {kgCreateModalTitle(kgModalColumnType)}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isKGModalOpen && (
              <CreateKGColumnComponent
                key={`${kgModalColumnIndex}-${kgModalColumnType}-${kgModalDefaultName}`}
                defaultName={kgModalDefaultName}
                colType={kgModalColumnType}
                refreshKGColumns={refreshKGColumns}
                onClose={onKGModalClose}
                onSuccess={handleKGColumnCreated}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Metric creation modal — pre-selects the triggering logical column as the observation column.
          updateFlag=true  → creates immediately via API (onSuccess auto-maps).
          updateFlag=false → defers to metricRequestList; created after model save (onDefer queues). */}
      <Modal isOpen={isMetricModalOpen} onClose={onMetricModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Metric</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" maxH="80vh">
            {isMetricModalOpen && (
              <MetricDetailFormContainer
                createMetricFlag={true}
                modelInfo={{ ...(modelInfo ?? {}), columns } as ModelInfo}
                metricHeader={null}
                onClose={onMetricModalClose}
                onSuccess={updateFlag ? handleMetricCreated : undefined}
                onDefer={!updateFlag ? handleMetricDeferred : undefined}
                defaultObservationColumn={
                  metricModalLogicalColumn?.header?.id
                    ? ObservationColumnProto.create({
                        logical_column_header: { id: metricModalLogicalColumn.header.id },
                        aggregation: metricModalLogicalColumn.default_aggregation ?? AggregationType.SUM,
                      })
                    : undefined
                }
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OntologyMapping;
